import React, { ReactNode, useState } from 'react';

import { check } from '@/assets/icons';
import { UserAvatar } from '@/components/UserAvatar';
import UserAvatarGroup from '@/components/UserAvatar/UserAvatarGroup';
import { LinkFrom } from '@/config/constants';
import { classNames } from '@/helpers/classNames';
import constants from '@/theme/exportedVariables.module.scss';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { ButtonBase, SvgIcon, useMediaQuery } from '@material-ui/core';
import { Link } from 'react-router-dom';

import { AsaphAvatar, AsaphAvatarGroup } from '../Avatar';
import { NotificationBadge } from '../NotificationBadge';
import styles from './ChatCard.module.scss';

export type ChatCardProps = {
	type: 'private' | 'group';
	membersNames: string[] | undefined;
	recipientIds: string[]; // The list of chat users without the current user
	userIds: string[]; // The full list of chat users
	chatTitle: string;
	link?: string;
	linkFrom?: LinkFrom;
	lastMessage: string | undefined;
	lastMessageAuthor: string | undefined;
	isAuthorHidden?: boolean;
	lastMessageTimestamp?: string;
	hasNotification?: boolean;
	isOnline: boolean;
	avatarOnly?: boolean;
} & Pick<BaseChatCardProps, 'selectable' | 'checked' | 'onClick'>;

export const ChatCard: React.FC<ChatCardProps> = (props: ChatCardProps) => {
	if (props.type === 'private') {
		return (
			<BaseChatCard
				link={props.link}
				linkFrom={props.linkFrom}
				image={
					<UserAvatar
						size="small"
						activeSize="small"
						notificationSize="small"
						userId={props.recipientIds[0]}
					/>
				}
				lastMessageAuthor={props.lastMessageAuthor}
				title={props.chatTitle}
				hasNotification={props.hasNotification}
				messageText={props.lastMessage}
				dateSubtitle={props.lastMessageTimestamp}
				isOnline={props.isOnline}
				isAuthorHidden={props.isAuthorHidden}
				isPrivate
				avatarOnly={props.avatarOnly}
				selectable={props.selectable}
				checked={props.checked}
				onClick={props.onClick}
			/>
		);
	}

	return (
		<BaseChatCard
			link={props.link}
			linkFrom={props.linkFrom}
			image={
				<UserAvatarGroup
					size="small"
					notificationSize="small"
					userIds={[props.userIds[0], props.userIds[1]]}
					activeSize="small"
					userAvatarProps={{
						size: 'xx-small',
						activeSize: 'small',
						borderColor: 'sheet',
					}}
				/>
			}
			lastMessageAuthor={props.lastMessageAuthor}
			title={props.chatTitle}
			hasNotification={props.hasNotification}
			messageText={props.lastMessage}
			dateSubtitle={props.lastMessageTimestamp}
			isOnline={props.isOnline}
			avatarOnly={props.avatarOnly}
			selectable={props.selectable}
			checked={props.checked}
			onClick={props.onClick}
		/>
	);
};

export interface BaseChatCardProps {
	link?: string;
	linkFrom?: LinkFrom;
	image: ReactNode;
	title?: string;
	messageText?: string;
	dateSubtitle?: string;
	hasNotification?: boolean;
	isOnline?: boolean;
	isPrivate?: boolean;
	lastMessageAuthor?: string;
	isAuthorHidden?: boolean;
	linkComponentProp?: boolean;
	avatarOnly?: boolean;
	hasHover?: boolean;
	selectable?: boolean;
	checked?: boolean;
	onClick?: () => void;
}

export const BaseChatCard = ({
	link,
	linkFrom,
	linkComponentProp,
	isPrivate,
	hasNotification,
	image,
	messageText,
	title,
	dateSubtitle,
	isOnline,
	lastMessageAuthor,
	isAuthorHidden,
	avatarOnly,
	selectable,
	checked,
	hasHover = true,
	onClick,
}: BaseChatCardProps) => {
	const isTablet = useMediaQuery(`(min-width: ${constants.sizeTablet})`);

	const chatCardClasses = classNames([
		styles['chat-card'],
		hasNotification && styles['unread'],
		isTablet && hasHover && styles['hover'],
	]);

	const chatSubtitle = isAuthorHidden
		? messageText
		: lastMessageAuthor + ': ' + messageText;

	const avatar = (
		<NotificationBadge
			overlap="circular"
			type="onlineStatus"
			verticalPosition="bottom"
			horizontalPosition="right"
			variant="small"
			borderColor={hasNotification ? 'light' : 'regular'}
			invisible={!isOnline}
		>
			{image}
		</NotificationBadge>
	);

	const returnComponent = (
		<>
			{avatar}
			<div className={'hsb-10'} />
			<div className={styles['chat-card__content']}>
				<div className={styles['chat-card__title']}>{title}</div>
				<div
					className={classNames([
						styles['chat-card__subtitle'],
						hasNotification && styles['unread'],
					])}
				>
					<span className={styles['leading']}>{chatSubtitle}</span>
					<div className={styles['trailing-wrapper']}>
						<span className={styles['chat-card__middle-dot']}>
							&bull;
						</span>
						<span className={styles['trailing']}>
							{dateSubtitle}
						</span>
					</div>
				</div>
			</div>
			{hasNotification && (
				<>
					<div
						className={'hsb-10'}
					/>
					<div className={styles['chat-card__online-icon']} />
				</>
			)}
			{selectable && (
				<div className={styles['selectable']}>
					<div
						className={classNames([
							styles['card-icon-container'],
							checked && styles['checked'],
						])}
					>
						{checked && (
							<SvgIcon
								component={check}
								classes={{
									root: styles['card-icon'],
								}}
							/>
						)}
					</div>
				</div>
			)}
		</>
	);

	return !!link ? (
		<ButtonBase
			className={chatCardClasses}
			focusRipple
			component={Link}
			to={link}
			state={{
				isPrivate,
				linkFrom,
				shouldChatBeOpened: linkComponentProp,
			}}
		>
			{avatarOnly ? avatar : returnComponent}
		</ButtonBase>
	) : (
		<div
			className={chatCardClasses}
			onClick={() => {
				if (selectable) {
					Haptics.impact({ style: ImpactStyle.Medium });
				}
				onClick && onClick();
			}}
		>
			{returnComponent}
		</div>
	);
};

export default ChatCard;
